import React, { useContext, useState } from "react";
import { Text, Image, Box, Button, Modal, useDisclosure, ModalOverlay, ModalContent, VStack, Input, Textarea, Flex, Select, Toast, useToast } from "@chakra-ui/react";
import Form from "../custom/Form";
import { UseFormReturnType, useForm } from "@mantine/form";
import { PricingType } from "./AddProductModal";
import { useMutation } from "react-query";
import { AdminContext } from "../../contexts/AdminContext";
import { DefaultPricing } from "./DefaultPricing";
import { CustomPricing } from "./CustomPricing";
import { deleteProduct, updateProduct } from "../../services/products";

const Product = ({ data }: any) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <React.Fragment>
      <Box
        px={4}
        p={4} borderRadius='10px'
        boxShadow='md' 
        display="flex"
        flexDirection="column" // Align items vertically
      >
        <Box overflow='hidden' borderRadius="10px">
          <Image
            w="100%"
            h="10rem"
            bgPosition="center"
            objectFit="cover"
            objectPosition="center"
            sx={{
              transition: 'transform 1s linear' 
            }}
            _hover={{
              transform: 'scale(1.1)',
              filter: 'brightness(70%)',
            }}
            src={data.images[0]?.url || 'https://fakeimg.pl/600x400?text=No+Image'}
            borderRadius="10px"
          />
        </Box>

        <Box>
          <Text
            fontWeight='semibold' mt={2}
            fontSize='lg' color='gray.500'
          >
            {data.name}
          </Text>

          <Text
            fontWeight='semibold' my={2}
            fontSize='md' color='#B5BAC1'
            noOfLines={2} // Limit the text to two lines
          >
            {data.description}
          </Text>
        </Box>

        <Box mt='auto' width="100%">
          <Button
            onClick={onOpen}
            type="submit"
            bg='#37A75B' size="md"
            w='100%' color='white'
            _hover={{ backgroundColor: '#046425' }}
          >
            Edit Product
          </Button>
        </Box>
      </Box>

      <Modal size='xl' isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <EditProductForm product={data}/>
        </ModalContent>
      </Modal>
    </React.Fragment>
  )
}

export default Product

type EditProductFormProps = {
  product: {
    id: string;
    name: string;
    description: string;
  }
}

export interface UpdateProductPayloadType {
  name: string;
  description: string;
  price: any[]
}

const EditProductForm = ({ product }: EditProductFormProps) => {
  const toast = useToast()
  const { admin } = useContext(AdminContext)
  const [pricingType, setPricingType] = useState<PricingType>('default')

  const InitialValues: UpdateProductPayloadType = {
    name: product.name,
    description: product.description,
    price: [],
  }

  const form = useForm({
    initialValues: InitialValues,

    validate: {
      name: (value) => (
        !value ? 'Product name is required' : null
      ),
      description: (value) => (
        !value ? 'Product description must be at least 60 characters' : null
      ),
      price: (value) => (
        value.length < 1 ? 'Please add product pricing' : null
      )
    },
  });

  const mutation = useMutation((data: UpdateProductPayloadType) => updateProduct(product.id, `bearer ${admin.token}`, data), {
    onError: (error: any) => {
      toast({
        title: error.response.data.message,
        status: 'error',
        isClosable: true,
        duration: 2000,
      })
    },

    onSuccess: () => {
      toast({
        title: "Product updated",
        status: 'success',
        isClosable: true,
        duration: 2000
      })

      form.reset()
    },
  })

  const deleteMutation = useMutation(() => deleteProduct(product.id, `bearer ${admin.token}`), {
    onError: (error: any) => {
      toast({
        title: error.response.data.message,
        status: 'error',
        isClosable: true,
        duration: 2000,
      })
    },

    onSuccess: () => {
      toast({
        title: "Product deleted",
        status: 'success',
        isClosable: true,
        duration: 2000
      })
    },
  })

  const handleMutation = (values: UpdateProductPayloadType) => {
    mutation.mutate(values)
  }

  return (
    <Form onSubmit={form.onSubmit((values) => handleMutation(values))}>
      <Box p={5}>
        <Text fontSize='2xl' fontWeight='semibold' color='gray.600'>
          Update Product
        </Text>

        <VStack w='100%' mt='5' spacing='5'>
          <Box w='100%'>
            <Text fontWeight='semibold' color='gray.500'>
              Product Name
            </Text>

            <Box mt='2'>
              <Input
                {...form.getInputProps('name')}
                disabled={mutation.isLoading}
                size='lg' mt={1} placeholder="Air Jordan Mid 1"
                border={form.errors.name ? '2px' : '1px'}
                borderColor={form.errors.name ? 'red.500' : 'gray.500'} color='gray.500'
                focusBorderColor={form.errors.name ? 'red.500' : 'gray.500'}
                _hover={{ outlineColor: 'transparent' }}
              />

              {form.errors.name &&
                <Text fontWeight='semibold' mt={1} color='red.500'>
                  {form.errors.name}
                </Text>
              }
            </Box>
          </Box>

          <Box w='100%'>
            <Text fontWeight='semibold' color='gray.500'>
              Product Description
            </Text>

            <Box mt='2'>
              <Textarea
                {...form.getInputProps('description')}
                disabled={mutation.isLoading} maxH='150px'
                size='lg' mt={1} placeholder="This iteration of the AJ1 reimagines Mike's first signature model with a fresh mix of colors."
                border={form.errors.description ? '2px' : '1px'}
                borderColor={form.errors.description ? 'red.500' : 'gray.500'} color='gray.500'
                focusBorderColor={form.errors.description ? 'red.500' : 'gray.500'}
                _hover={{ outlineColor: 'transparent' }}
              />

              {form.errors.description &&
                <Text fontWeight='semibold' mt={1} color='red.500'>
                  {form.errors.description}
                </Text>
              }
            </Box>
          </Box>

          <Box w='100%'>
            <Flex justifyContent='space-between' alignItems='start'>
              <Box w='100%'>
                <Text fontWeight='semibold' color='gray.500'>
                  Product Price
                </Text>
              </Box>

              <Select
                color='gray.500'
                border='1px'
                w='200px'
                borderColor='gray.500'
                focusBorderColor='#37A75B'
                value={pricingType}
                onChange={({ target }) => {
                  setPricingType(target.value as PricingType)
                }}
              >
                <option value='default'>Default</option>
                <option value='custom'>Custom</option>
              </Select>
            </Flex>

            <Box mt='2'>
              {pricingType === 'default' ? (
                <DefaultPricing
                  form={form}
                  mutation={mutation}
                />
              ) : (
                <CustomPricing
                  form={form}
                  mutation={mutation}
                />
              )}
            </Box>
          </Box>

          <Box w='100%' mt={5}>
            <Button
              isLoading={mutation.isLoading}
              type='submit'
              bg='#37A75B' size="lg"
              w='100%' color='white'
              _hover={{ backgroundColor: mutation.isLoading ? '#37A75B' : '#046425' }}
            >
              Update Product
            </Button>
          </Box>

          <Box w='100%' mt={5}>
            <Button
              isLoading={deleteMutation.isLoading}
              type='button'
              colorScheme="red"
              onClick={() => deleteMutation.mutate()}
              size="lg"
              w='100%' color='white'
            >
              Delete Product
            </Button>
          </Box>
        </VStack>
      </Box>
    </Form>
  )
}